import {useEffect, useMemo, useState} from "react";
import {Box, Paper} from "@mui/material";
import {useEventEmitter2} from "./util/useEventEmitter2";
import {useStatusHints} from "./StatusHintContext";
import {EventEmitter2} from "./util/EventEmitter2";
import {ElevationApp} from "./ElevationApp";
import {DebugVarsPopover} from "./DebugVarsPopover";
import {DragStatus} from "./scenes/frameGrid/Util";

// A thin status strip under the canvas (mirroring the MenuBar on top). For now it
// surfaces one keyboard hint at a time in the bottom-left, subtle enough that
// power users pick shortcuts up over time without it demanding attention. The
// right side hangs status/debug affordances (variable & formula inspector).
export function StatusBar(props: {app: EventEmitter2<ElevationApp | null>}) {
    const ctx = useStatusHints();
    const map = useEventEmitter2(ctx?.hints);

    // dedupe to a stable list; `sig` changes only when the set of hints changes.
    const list = useMemo(() => Array.from(new Set(map ? map.values() : [])), [map]);
    const sig = list.join("|");

    // rotate only when the list changes: pick a fresh random hint then hold it.
    const [shown, setShown] = useState<string>();
    useEffect(() => {
        if(list.length === 0) {
            setShown(undefined);
            return;
        }
        setShown(list[Math.floor(Math.random() * list.length)]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sig]);

    const app = useEventEmitter2(props.app);
    const drag = useEventEmitter2(app?.dragStatus);

    return (
        <Paper square elevation={0} sx={{borderTop: 1, borderColor: "divider", flexShrink: 0}}>
            <Box sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                minHeight: 20,
                px: 1,
                fontSize: "0.7rem",
                lineHeight: 1,
                color: "text.secondary",
            }}>
                <span>{shown}</span>
                <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                    <DragStatusText status={drag ?? null} />
                    {import.meta.env.DEV && <SelectionText app={props.app} />}
                    <SelectedGlassLabelsText app={props.app} />
                    <DebugVarsPopover app={props.app} />
                </Box>
            </Box>
        </Paper>
    );
}

// dev only: the model ids of the selected elements, so a bug report can name them.
function SelectionText(props: {app: EventEmitter2<ElevationApp | null>}) {
    const app = useEventEmitter2(props.app);
    const selection = useEventEmitter2(app?.viewSettings.selection);
    if(!selection || selection.length === 0) return null;

    return (
        <Box component="span" sx={{fontFamily: "monospace"}}>
            {selection.map(s => `${s.type} ${s.id}`).join(", ")}
        </Box>
    );
}

// the glass sizes of the selected lites.
function SelectedGlassLabelsText(props: {app: EventEmitter2<ElevationApp | null>}) {
    const app = useEventEmitter2(props.app);
    const labels = useEventEmitter2(app?.selectedGlassLabels.labels);
    if(!labels || labels.size === 0) return null;

    return (
        <Box component="span" sx={{color: "text.primary", fontWeight: 500}}>
            {Array.from(labels.values()).join(", ")}
        </Box>
    );
}

// a status light for stick drags: green while idle or while the dragged stick is in a
// valid position (with what the release will do, if anything), and a warning on a
// washed-out orange when the release will revert the drag.
function DragStatusText(props: {status: DragStatus | null}) {
    if(props.status?.tone === "error") {
        return (
            <Box component="span" sx={{bgcolor: "#ffe0b2", color: "#000", fontWeight: 500, px: 0.75, py: 0.25, borderRadius: 0.5}}>
                {props.status.text}
            </Box>
        );
    }

    return (
        <Box component="span" sx={{display: "flex", alignItems: "center", gap: 0.5, color: "text.primary"}}>
            <Box component="span" sx={{display: "inline-block", width: 8, height: 8, borderRadius: "50%", bgcolor: "success.main"}} />
            {props.status?.text}
        </Box>
    );
}
